import React, { useState, useEffect, useCallback } from "react";
import { crmDataService } from "../utils/crmDataService";
import ConditionalFields from "./ConditionalFields";
import apiClient from "../../../../apiClient";

function TicketForm({
  teams,
  stores,
  onTicketCreated,
  viewMode = false,
  ticketData = null,
  ticketFieldValues = null,
  pendingApproval = null,
  onApprovalAction = null,
  // New props for locking team/category
  defaultTeamCode = null,
  lockTeam = false,
  defaultCategoryId = null,
  defaultCategoryName = null,
  lockCategory = false,
  excludeCategoryNames = [],
  excludeTeamCodes = [],
}) {
  // Helper to extract store PC from dropdown value
  const extractStorePC = (storeValue) => {
    if (!storeValue || typeof storeValue !== "string") {
      return storeValue;
    }

    // If format is "300231 - Store Name", extract just "300231"
    if (storeValue.includes(" - ")) {
      return storeValue.split(" - ")[0].trim();
    }

    return storeValue.trim();
  };

  const [formData, setFormData] = useState({
    teamCode: defaultTeamCode || "",
    categoryId: "",
    subcategoryId: "",
    storePC: "",
    subject: "",
    description: "",
    priority: "Normal",
    requesterEmail:
      localStorage.getItem("userEmail") || "",
    requesterName: localStorage.getItem("userName") || "",
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [conditionalFields, setConditionalFields] = useState([]);
  const [conditionalFieldValues, setConditionalFieldValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assignmentPreview, setAssignmentPreview] = useState(null);

  // Fetch current user data from API on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await apiClient.get("/api/users/me");
        const userData = response.data;

        // Update form data with user info from API
        setFormData((prev) => ({
          ...prev,
          requesterEmail: userData.email || prev.requesterEmail,
          requesterName: userData.name || prev.requesterName,
        }));

        // Also update localStorage for future use
        if (userData.email) {
          localStorage.setItem("userEmail", userData.email);
        }
        if (userData.name) {
          localStorage.setItem("userName", userData.name);
        }
      } catch (err) {
        console.error("Error fetching current user:", err);
        // Fall back to localStorage values (already set in initial state)
      }
    };

    // Only fetch if not in view mode (creating new ticket)
    if (!viewMode) {
      fetchCurrentUser();
    }
  }, [viewMode]);

  // Populate form when in view mode
  useEffect(() => {
    if (viewMode && ticketData) {
      setFormData({
        teamCode: ticketData.team_code || "",
        categoryId: ticketData.category_id || "",
        subcategoryId: ticketData.subcategory_id || "",
        storePC: ticketData.store_pc || "",
        subject: ticketData.title || "",  // Database column is 'title', not 'subject'
        description: ticketData.description || "",
        priority: ticketData.priority || "Normal",
        requesterEmail: ticketData.requester_email || "",
        requesterName: ticketData.requester_name || "",
      });

      // Set conditional field values if provided
      if (ticketFieldValues) {
        // Transform nested {value, name, type} to flat values
        const flatValues = {};
        Object.entries(ticketFieldValues).forEach(([key, val]) => {
          flatValues[key] = val?.value ?? val;
        });
        setConditionalFieldValues(flatValues);
      }

      // Set assignment preview if available
      if (ticketData.assigned_to_name && ticketData.assigned_to) {
        setAssignmentPreview({
          name: ticketData.assigned_to_name,
          email: ticketData.assigned_to,
        });
      }
    } else {
      // Reset to default when exiting view mode
      setFormData({
        teamCode: defaultTeamCode || "",
        categoryId: "",
        subcategoryId: "",
        storePC: "",
        subject: "",
        description: "",
        priority: "Normal",
        requesterEmail: localStorage.getItem("userEmail") || "",
        requesterName: localStorage.getItem("userName") || "",
      });
      setConditionalFieldValues({});
      setAssignmentPreview(null);
    }
  }, [viewMode, ticketData, ticketFieldValues, defaultTeamCode]);

  // Auto-select category when defaultCategoryId or defaultCategoryName is provided and categories are loaded
  useEffect(() => {
    if ((defaultCategoryId || defaultCategoryName) && categories.length > 0 && !viewMode) {
      let categoryToSelect = null;

      // First try to find by ID
      if (defaultCategoryId) {
        categoryToSelect = categories.find(cat => cat.id === defaultCategoryId || cat.id === parseInt(defaultCategoryId));
      }

      // If not found by ID, try to find by name
      if (!categoryToSelect && defaultCategoryName) {
        categoryToSelect = categories.find(cat =>
          cat.name?.toLowerCase() === defaultCategoryName.toLowerCase()
        );
      }

      if (categoryToSelect) {
        setFormData(prev => ({
          ...prev,
          categoryId: categoryToSelect.id
        }));
      }
    }
  }, [defaultCategoryId, defaultCategoryName, categories, viewMode]);

  // Load categories when team changes
  const loadCategories = useCallback(async () => {
    try {
      const team = teams.find((t) => t.code === formData.teamCode);
      if (team) {
        const cats = await crmDataService.getCategoriesByTeam(team.id);
        setCategories(cats);
      }
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  }, [formData.teamCode, teams]);

  // Load subcategories when category changes
  const loadSubcategories = useCallback(async () => {
    try {
      const subs = await crmDataService.getSubcategoriesByCategory(
        formData.categoryId
      );
      setSubcategories(subs);
    } catch (err) {
      console.error("Failed to load subcategories:", err);
    }
  }, [formData.categoryId]);

  // NEW: Load conditional fields when subcategory changes
  const loadConditionalFields = useCallback(async () => {
    try {
      if (!formData.subcategoryId) {
        setConditionalFields([]);
        if (!viewMode) {
          setConditionalFieldValues({});
        }
        return;
      }

      const fields = await crmDataService.getFieldsBySubcategory(
        formData.subcategoryId
      );
      setConditionalFields(fields);

      // Only initialize with defaults if NOT in view mode
      if (!viewMode) {
        const initialValues = {};
        fields.forEach((field) => {
          if (field.default_value) {
            initialValues[field.field_code] = field.default_value;
          }
        });
        setConditionalFieldValues(initialValues);
      }

      setFieldErrors({});
    } catch (err) {
      console.error("Failed to load conditional fields:", err);
      setConditionalFields([]);
    }
  }, [formData.subcategoryId, viewMode]);

  // Test assignment when key fields change
  const testAssignment = useCallback(async () => {
    try {
      const result = await crmDataService.testAssignment({
        teamCode: formData.teamCode,
        categoryId: formData.categoryId || null,
        subcategoryId: formData.subcategoryId || null,
        storePC: formData.storePC,
      });
      setAssignmentPreview(result.assignment);
    } catch (err) {
      setAssignmentPreview(null);
    }
  }, [
    formData.teamCode,
    formData.categoryId,
    formData.subcategoryId,
    formData.storePC,
  ]);

  useEffect(() => {
    if (formData.teamCode) {
      loadCategories();
    } else {
      setCategories([]);
      setSubcategories([]);
      if (!viewMode) {
        setConditionalFields([]);
      }
    }
  }, [formData.teamCode, loadCategories, viewMode]);

  useEffect(() => {
    if (formData.categoryId) {
      loadSubcategories();
    } else {
      setSubcategories([]);
      if (!viewMode) {
        setConditionalFields([]);
      }
    }
  }, [formData.categoryId, loadSubcategories, viewMode]);

  // NEW: Load conditional fields when subcategory changes
  useEffect(() => {
    if (!viewMode || (viewMode && formData.subcategoryId)) {
      loadConditionalFields();
    }
  }, [formData.subcategoryId, loadConditionalFields, viewMode]);

  useEffect(() => {
    if (!viewMode && formData.teamCode && formData.storePC) {
      testAssignment();
    }
  }, [
    formData.teamCode,
    formData.categoryId,
    formData.subcategoryId,
    formData.storePC,
    testAssignment,
    viewMode,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selectedTeam = teams.find((t) => t.code === formData.teamCode);
    console.log("=== SUBMITTING TICKET ===");
    console.log("Selected team from dropdown:", selectedTeam);
    console.log("Form data being sent:", {
      teamCode: formData.teamCode,
      teamId: formData.teamId, // This will be undefined
      categoryId: formData.categoryId,
      subcategoryId: formData.subcategoryId,
    });

    setLoading(true);
    setError("");
    setFieldErrors({});

    try {
      // Validate conditional fields if they exist
      if (formData.subcategoryId && conditionalFields.length > 0) {
        const validation = await crmDataService.validateFieldValues(
          formData.subcategoryId,
          conditionalFieldValues
        );

        if (!validation.valid) {
          // Map validation errors to field codes
          const errors = {};
          validation.errors.forEach((err) => {
            // Extract field name from error message
            const match = err.match(/^(.+?) (is required|must be)/);
            if (match) {
              const fieldName = match[1];
              const field = conditionalFields.find(
                (f) => f.field_name === fieldName
              );
              if (field) {
                errors[field.field_code] = err;
              }
            }
          });
          setFieldErrors(errors);
          setError("Please fix the validation errors above");
          setLoading(false);
          return;
        }
      }

      // Process conditional fields - extract store PCs from store-related fields
      const processedConditionalFields = {};
      Object.keys(conditionalFieldValues).forEach((fieldKey) => {
        const value = conditionalFieldValues[fieldKey];

        // If field name contains "store" or "Store", extract PC
        if (
          fieldKey.toLowerCase().includes("store") &&
          typeof value === "string"
        ) {
          processedConditionalFields[fieldKey] = extractStorePC(value);
        } else {
          processedConditionalFields[fieldKey] = value;
        }
      });

      // Create ticket with extracted store PCs
      const ticket = await crmDataService.createTicket({
        ...formData,
        storePC: extractStorePC(formData.storePC), // Extract PC from main store field
        conditionalFields: processedConditionalFields,
      });

      onTicketCreated(ticket);

      // Reset form (preserve locked team/category)
      setFormData({
        teamCode: defaultTeamCode || "",
        categoryId: "",
        subcategoryId: "",
        storePC: "",
        subject: "",
        description: "",
        priority: "Normal",
        requesterEmail: formData.requesterEmail,
        requesterName: formData.requesterName,
      });
      setConditionalFieldValues({});
      setConditionalFields([]);
      setAssignmentPreview(null);
    } catch (err) {
      // Extract error message from different possible error formats
      const errorMessage =
        err.response?.data?.error?.message || // Backend error object
        err.response?.data?.error || // Backend error string
        err.message || // Standard error
        "An error occurred"; // Fallback
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Reset dependent fields
    if (name === "teamCode") {
      setCategories([]);
      setSubcategories([]);
      setConditionalFields([]);
      setFormData((prev) => ({
        ...prev,
        categoryId: "",
        subcategoryId: "",
      }));
    } else if (name === "categoryId") {
      setSubcategories([]);
      setConditionalFields([]);
      setFormData((prev) => ({
        ...prev,
        subcategoryId: "",
      }));
    }
  };

  // NEW: Handle conditional field changes
  const handleConditionalFieldChange = (fieldCode, value) => {
    setConditionalFieldValues((prev) => ({
      ...prev,
      [fieldCode]: value,
    }));

    // Clear error for this field
    if (fieldErrors[fieldCode]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldCode];
        return newErrors;
      });
    }
  };

  return (
    <div className="ticket-form-container">
      <form onSubmit={handleSubmit} className="ticket-form">
        {/* Approval Request Section */}
        {viewMode && pendingApproval && (
          <div className="approval-request-section">
            <div className="approval-header">
              <h3>⚠️ Approval Required</h3>
              <span className="approval-role-badge">
                {pendingApproval.role_label || "Your Action Needed"}
              </span>
            </div>
            <div className="approval-info">
              <p>This ticket requires your approval before it can proceed.</p>
            </div>
            <div className="approval-actions">
              <button
                type="button"
                className="btn-approve"
                onClick={() =>
                  onApprovalAction && onApprovalAction(pendingApproval, "approve")
                }
              >
                ✓ Approve
              </button>
              <button
                type="button"
                className="btn-reject"
                onClick={() =>
                  onApprovalAction && onApprovalAction(pendingApproval, "reject")
                }
              >
                ✗ Reject
              </button>
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Team *</label>
            <select
              name="teamCode"
              value={formData.teamCode}
              onChange={handleChange}
              required
              disabled={viewMode || lockTeam}
            >
              <option value="">Select Team</option>
              {teams && teams.length > 0 ? (
                teams
                  .filter((team) => !excludeTeamCodes.includes(team.code))
                  .map((team) => (
                    <option key={team.id} value={team.code}>
                      {team.name}
                    </option>
                  ))
              ) : (
                <option disabled>No teams available</option>
              )}
            </select>
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              name="categoryId"
              value={formData.categoryId}
              onChange={handleChange}
              disabled={viewMode || lockCategory || !categories.length}
            >
              <option value="">Select Category</option>
              {categories
                .filter((cat) => !excludeCategoryNames.some(
                  (excluded) => excluded.toLowerCase() === cat.name?.toLowerCase()
                ))
                .map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="form-group">
            <label>Subcategory</label>
            <select
              name="subcategoryId"
              value={formData.subcategoryId}
              onChange={handleChange}
              disabled={viewMode || !subcategories.length}
            >
              <option value="">Select Subcategory</option>
              {subcategories.map((sub) => (
                <option key={sub.id} value={sub.id}>
                  {sub.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* NEW: Conditional Fields Section */}
        {conditionalFields.length > 0 && (
          <ConditionalFields
            fields={conditionalFields}
            values={conditionalFieldValues}
            onChange={handleConditionalFieldChange}
            errors={fieldErrors}
            stores={stores}
            readOnly={viewMode}
          />
        )}

        <div className="form-row">
          <div className="form-group">
            <label>Store *</label>
            <select
              name="storePC"
              value={formData.storePC}
              onChange={handleChange}
              required
              disabled={viewMode}
            >
              <option value="">Select Store</option>
              {stores && stores.length > 0 ? (
                [...stores]
                  .sort((a, b) => a.store_name.localeCompare(b.store_name))
                  .map((store) => (
                    <option key={store.store_pc} value={store.store_pc}>
                      {store.store_name}
                    </option>
                  ))
              ) : (
                <option disabled>No stores available</option>
              )}
            </select>
          </div>

          <div className="form-group">
            <label>Priority</label>
            <select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              disabled={viewMode}
            >
              <option value="Low">Low</option>
              <option value="Normal">Normal</option>
              <option value="High">High</option>
              <option value="Urgent">Urgent</option>
            </select>
          </div>
        </div>

        {/* HR Generalist Assignment Info */}
        {assignmentPreview && formData.storePC && !viewMode && (
          <div className="hr-generalist-info">
            <div className="hr-generalist-icon">👤</div>
            <div className="hr-generalist-text">
              Your HR Generalist helping you is <strong>{assignmentPreview.name}</strong>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Subject *</label>
          <input
            type="text"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            placeholder="Brief description of the issue"
            disabled={viewMode}
          />
        </div>

        <div className="form-group">
          <label>Description *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows="5"
            placeholder="Detailed description of the request"
            disabled={viewMode}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Requester Email *</label>
            <input
              type="email"
              name="requesterEmail"
              value={formData.requesterEmail}
              onChange={handleChange}
              required
              disabled={viewMode}
            />
          </div>

          <div className="form-group">
            <label>Requester Name *</label>
            <input
              type="text"
              name="requesterName"
              value={formData.requesterName}
              onChange={handleChange}
              required
              disabled={viewMode}
            />
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}

        {!viewMode && (
          <div className="form-actions">
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? "Creating..." : "Create Ticket"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

export default TicketForm;
